import { computed, inject, Injectable, signal } from '@angular/core';
import { DateService, IsoDate } from '@softline/core';
import { PromotionFilter, QueryShelfLabelRequest } from '../types';

@Injectable()
export class ShelfLabelQueryStateService {
  private readonly dateService = inject(DateService);

  readonly referenceDate = signal<IsoDate>(this.dateService.now());
  readonly storeId = signal<string | null>(null);
  readonly priceLevelId = signal<string | null>(null);
  readonly promotionFilter = signal<PromotionFilter>('ALL');
  readonly onlyProductsWithStockQuantity = signal<boolean>(false);
  readonly onlyProductsWithStorageLocation = signal<boolean>(false);
  readonly supplierId = signal<string | null>(null);
  readonly productMainGroupId = signal<string | null>(null);
  readonly productSubGroupId = signal<string | null>(null);
  // TODO: no field-ok component exists yet for a third product-group level — wired
  // here for parity with the request shape, but there's no UI control to set it.
  readonly productSubSubGroupId = signal<string | null>(null);

  // Undefined until storeId (resolved from the session) and priceLevelId
  // (a required manual pick, no default) are both set.
  readonly request = computed<QueryShelfLabelRequest | undefined>(() => {
    const storeId = this.storeId();
    const priceLevelId = this.priceLevelId();
    if (!storeId || !priceLevelId) return undefined;

    return {
      storeId: Number(storeId),
      priceLevelId: Number(priceLevelId),
      promotionFilter: this.promotionFilter(),
      onlyProductsWithStockQuantity: this.onlyProductsWithStockQuantity(),
      onlyProductsWithStorageLocation: this.onlyProductsWithStorageLocation(),
      supplierId: this.supplierId() ?? undefined,
      productMainGroupId: this.productMainGroupId() ?? undefined,
      productSubGroupId: this.productSubGroupId() ?? undefined,
      productSubSubGroupId: this.productSubSubGroupId() ?? undefined,
    };
  });

  readonly confirmedRequest = signal<QueryShelfLabelRequest | undefined>(undefined);

  confirm(): void {
    const draft = this.request();
    if (draft) this.confirmedRequest.set(draft);
  }

  reset(): void {
    this.referenceDate.set(this.dateService.now());
    this.priceLevelId.set(null);
    this.promotionFilter.set('ALL');
    this.onlyProductsWithStockQuantity.set(false);
    this.onlyProductsWithStorageLocation.set(false);
    this.supplierId.set(null);
    this.productMainGroupId.set(null);
    this.productSubGroupId.set(null);
    this.productSubSubGroupId.set(null);
    this.confirm();
  }
}
